/**
 * Data Room Document Indexer
 *
 * Service for indexing documents from connected data rooms (Google Drive, Dropbox)
 * for RAG retrieval in DD Chat.
 */

import type { GoogleDriveFile } from '@/lib/google-drive/types';
import type { DropboxFile } from '@/lib/dropbox/types';
import { downloadFileAsBase64 as downloadGoogleDriveFile } from '@/lib/google-drive/api';
import { downloadFileAsBase64 as downloadDropboxFile } from '@/lib/dropbox/api';
import { indexDocument, type IndexDocumentResponse } from '@/lib/supabase/edge-functions';
import { reportError } from '@/lib/errors';

// ============================================
// Types
// ============================================

export type DataRoomProvider = 'google-drive' | 'dropbox' | 'local';

export interface IndexableDocument {
  id: string;
  name: string;
  mimeType: string;
  provider: DataRoomProvider;
  /** For Google Drive: file ID, for Dropbox: file path */
  providerRef: string;
  /** Size in bytes (if known) */
  size?: number;
  /** Last modified date (if known) */
  modifiedAt?: string;
}

export interface IndexingResult {
  documentId: string;
  success: boolean;
  chunkCount?: number;
  processingTimeMs?: number;
  error?: string;
}

export interface BatchIndexingResult {
  total: number;
  successful: number;
  failed: number;
  results: IndexingResult[];
}

export interface IndexedDocumentRecord {
  documentId: string;
  startupId: string;
  provider: DataRoomProvider;
  providerRef: string;
  fileName: string;
  indexedAt: string;
  chunkCount: number;
}

// Track indexed documents to avoid re-processing
const indexedDocuments = new Map<string, IndexedDocumentRecord>();

// ============================================
// Main API
// ============================================

/**
 * Index a single data room document
 *
 * Downloads the file from the cloud provider and processes it through
 * the indexing pipeline for RAG retrieval.
 *
 * @param file - Document to index
 * @param startupId - Associated startup ID
 * @returns Indexing result
 */
export async function indexDataRoomDocument(
  file: IndexableDocument,
  startupId: string
): Promise<IndexingResult> {
  const documentId = generateDocumentId(file, startupId);

  // Check if already indexed
  if (isDocumentIndexed(documentId)) {
    return {
      documentId,
      success: true,
      chunkCount: indexedDocuments.get(documentId)?.chunkCount,
    };
  }

  // Check if file type is supported
  if (!isSupportedFileType(file.mimeType, file.name)) {
    return {
      documentId,
      success: false,
      error: `Unsupported file type: ${file.mimeType}`,
    };
  }

  try {
    // Download file content based on provider
    const fileBase64 = await downloadFile(file);

    if (!fileBase64) {
      return {
        documentId,
        success: false,
        error: 'Failed to download file',
      };
    }

    // Call indexing edge function
    const response = await indexDocument({
      fileBase64,
      fileName: file.name,
      documentId,
      startupId,
      mimeType: file.mimeType,
      // 116.7-03: marks this as a data-room upload so index-document can trigger
      // claim extraction (flag-gated). Deck indexing omits this — deck claims are
      // owned by the scoring path, not re-extracted here.
      sourceType: 'data-room',
    });

    if (response.data?.success) {
      // Track indexed document
      indexedDocuments.set(documentId, {
        documentId,
        startupId,
        provider: file.provider,
        providerRef: file.providerRef,
        fileName: file.name,
        indexedAt: new Date().toISOString(),
        chunkCount: response.data.chunkCount || 0,
      });

      return {
        documentId,
        success: true,
        chunkCount: response.data.chunkCount,
        processingTimeMs: response.data.processingTimeMs,
      };
    }

    return {
      documentId,
      success: false,
      error: response.error || response.data?.error || 'Indexing failed',
    };
  } catch (error) {
    reportError(error, { context: 'DocumentIndexer.indexDataRoomDocument', extra: { fileName: file.name } });
    return {
      documentId,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Index multiple documents in batch
 *
 * Processes documents in parallel with concurrency control.
 *
 * @param files - Documents to index
 * @param startupId - Associated startup ID
 * @param concurrency - Max concurrent indexing operations (default: 2)
 * @returns Batch indexing result
 */
export async function indexDataRoomDocuments(
  files: IndexableDocument[],
  startupId: string,
  concurrency: number = 2
): Promise<BatchIndexingResult> {
  const results: IndexingResult[] = [];
  let successful = 0;
  let failed = 0;

  // Filter to supported file types
  const supportedFiles = files.filter((f) =>
    isSupportedFileType(f.mimeType, f.name)
  );


  // Process in batches for concurrency control
  for (let i = 0; i < supportedFiles.length; i += concurrency) {
    const batch = supportedFiles.slice(i, i + concurrency);

    const batchResults = await Promise.all(
      batch.map((file) => indexDataRoomDocument(file, startupId))
    );

    for (const result of batchResults) {
      results.push(result);
      if (result.success) {
        successful++;
      } else {
        failed++;
      }
    }
  }

  return {
    total: supportedFiles.length,
    successful,
    failed,
    results,
  };
}

/**
 * Convert Google Drive file to IndexableDocument
 */
export function googleDriveFileToIndexable(
  file: GoogleDriveFile
): IndexableDocument {
  return {
    id: file.id,
    name: file.name,
    mimeType: file.mimeType,
    provider: 'google-drive',
    providerRef: file.id,
    size: file.size ? parseInt(file.size, 10) : undefined,
    modifiedAt: file.modifiedTime,
  };
}

/**
 * Convert Dropbox file to IndexableDocument
 */
export function dropboxFileToIndexable(file: DropboxFile): IndexableDocument {
  return {
    id: file.id,
    name: file.name,
    mimeType: getMimeTypeFromFileName(file.name),
    provider: 'dropbox',
    providerRef: file.pathLower,
    size: file.size,
    modifiedAt: file.serverModified,
  };
}

/**
 * Check if a document has already been indexed
 */
export function isDocumentIndexed(documentId: string): boolean {
  return indexedDocuments.has(documentId);
}

/**
 * Get all indexed documents for a startup
 */
export function getIndexedDocuments(
  startupId: string
): IndexedDocumentRecord[] {
  return Array.from(indexedDocuments.values()).filter(
    (doc) => doc.startupId === startupId
  );
}

/**
 * Clear indexing cache for a startup
 * (Use when documents need to be re-indexed)
 */
export function clearIndexingCache(startupId?: string): void {
  if (startupId) {
    for (const [key, doc] of indexedDocuments) {
      if (doc.startupId === startupId) {
        indexedDocuments.delete(key);
      }
    }
  } else {
    indexedDocuments.clear();
  }
}

// ============================================
// Helper Functions
// ============================================

/**
 * Generate a unique document ID
 */
function generateDocumentId(file: IndexableDocument, startupId: string): string {
  return `${file.provider}-${startupId}-${file.id}`;
}

/**
 * Download file content from cloud provider
 */
async function downloadFile(
  file: IndexableDocument
): Promise<string | null> {
  switch (file.provider) {
    case 'google-drive':
      return downloadGoogleDriveFile(file.providerRef);

    case 'dropbox':
      return downloadDropboxFile(file.providerRef);

    case 'local':
      // Local files are handled separately (already have content)
      console.warn('[document-indexer] Local files not supported via this method');
      return null;

    default:
      reportError(new Error(`Unknown provider: ${file.provider}`), { context: 'DocumentIndexer.downloadFileContent', level: 'warning' });
      return null;
  }
}

/**
 * Check if file type is supported for indexing
 */
function isSupportedFileType(mimeType: string, fileName: string): boolean {
  // Supported MIME types
  const supportedMimeTypes = [
    'application/pdf',
    'text/plain',
    'text/markdown',
    'text/csv',
    'application/json',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/msword', // .doc
    'application/vnd.ms-excel', // .xls
    'application/vnd.ms-powerpoint', // .ppt
  ];

  // Check MIME type
  if (supportedMimeTypes.includes(mimeType)) {
    return true;
  }

  // Fallback to extension check
  const extension = fileName.split('.').pop()?.toLowerCase();
  const supportedExtensions = [
    'pdf',
    'txt',
    'md',
    'csv',
    'json',
    'docx',
    'xlsx',
    'pptx',
    'doc',
    'xls',
    'ppt',
  ];

  return extension ? supportedExtensions.includes(extension) : false;
}

/**
 * Infer MIME type from file name
 */
function getMimeTypeFromFileName(fileName: string): string {
  const extension = fileName.split('.').pop()?.toLowerCase();

  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    txt: 'text/plain',
    md: 'text/markdown',
    csv: 'text/csv',
    json: 'application/json',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    doc: 'application/msword',
    xls: 'application/vnd.ms-excel',
    ppt: 'application/vnd.ms-powerpoint',
  };

  return extension && mimeTypes[extension]
    ? mimeTypes[extension]
    : 'application/octet-stream';
}
